/**
 * /records/journals/:id/print?year= — a journal's entries laid out for paper
 * (the bound registration journal organisations still keep), in number
 * order, cancelled entries struck through with their reason.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { records } from '../lib/api.js';
import { fmtDay } from '../lib/journals.js';

const PAGE = 200;

export function JournalPrintPage() {
  const { t } = useTranslation('records');
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const year = searchParams.get('year');
  const [journal, setJournal] = useState(null);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await records.registers();
      const found = list.success ? list.response.find((j) => String(j.id) === String(id)) : null;
      if (!found) {
        if (!cancelled) setError(list.message || t('print.notFound'));
        return;
      }
      const all = [];
      for (let page = 1; ; page += 1) {
        const res = await records.registrations({ register_id: found.id, year: year ? Number(year) : undefined, page, size: PAGE });
        if (!res.success) {
          if (!cancelled) setError(res.message);
          return;
        }
        all.push(...res.response.items);
        if (all.length >= res.response.total || res.response.items.length === 0) break;
      }
      // The server lists newest first; a journal reads in number order.
      all.sort((a, b) => (a.reg_date === b.reg_date ? a.id - b.id : a.reg_date < b.reg_date ? -1 : 1));
      if (!cancelled) {
        setJournal(found);
        setRows(all);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, year, t]);

  if (error) return <p className="p-8 text-sm text-red-600" data-testid="print-error">{error}</p>;
  if (!rows) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="mx-auto max-w-[1100px] bg-white p-6 text-black print:p-0" data-testid="journal-print">
      <div className="mb-4 flex gap-2 print:hidden">
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-3.5 w-3.5" /> {t('print.back')}
        </Button>
        <Button size="sm" className="gap-1.5" onClick={() => window.print()}>
          <Printer className="h-3.5 w-3.5" /> {t('print.print')}
        </Button>
      </div>
      <h1 className="text-center text-lg font-bold">{journal.name}</h1>
      <p className="mb-4 text-center text-sm">{year ? t('print.year', { year }) : t('print.allYears')}</p>
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            {['number', 'date', 'correspondent', 'theirNumber', 'subject', 'signedBy', 'executor', 'case', 'note'].map((key) => (
              <th key={key} className="border border-black px-1.5 py-1 text-left align-top">{t(`print.columns.${key}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const cancelled = row.status === 'cancelled';
            return (
              <tr key={row.id} className={cancelled ? 'line-through' : ''}>
                <td className="border border-black px-1.5 py-1 align-top font-mono">{row.number}</td>
                <td className="border border-black px-1.5 py-1 align-top">{fmtDay(row.reg_date)}</td>
                <td className="border border-black px-1.5 py-1 align-top">{row.correspondent}</td>
                <td className="border border-black px-1.5 py-1 align-top">
                  {row.their_number}{row.their_date ? ` · ${fmtDay(row.their_date)}` : ''}
                </td>
                <td className="border border-black px-1.5 py-1 align-top">{row.subject}</td>
                <td className="border border-black px-1.5 py-1 align-top">{row.signed_by}</td>
                <td className="border border-black px-1.5 py-1 align-top">
                  {row.executor_name}{row.due_date ? ` · ${fmtDay(row.due_date)}` : ''}
                </td>
                <td className="border border-black px-1.5 py-1 align-top font-mono">{row.case_index}</td>
                <td className="border border-black px-1.5 py-1 align-top">
                  {cancelled ? t('entry.cancelledBecause', { reason: row.cancel_reason }) : row.note}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="mt-3 text-xs">{t('print.total', { count: rows.length })}</p>
    </div>
  );
}
